import { FATOR_COMISSAO_FORA_SUL } from '../comissao/foraSul';
/** Desconto isolado: não recalcula meta, faixa, venda, convergência ou deflator. */
export function calcularNegativoProdutividadeAtivacao(dados: {
  habilitada: boolean; prazoEncerrado: boolean; cargo: string; bateuMeta: boolean;
  basesPendentes: { pn: number; ha: number }; percentuais: { pn: number; ha: number };
}): number {
  if (!dados.habilitada || !dados.prazoEncerrado || dados.cargo !== 'CONSULTOR' || !dados.bateuMeta) return 0;
  const valores = [dados.basesPendentes.pn,dados.basesPendentes.ha,dados.percentuais.pn,dados.percentuais.ha];
  if (valores.some(v => !Number.isFinite(v) || v < 0)) throw new Error('Base ou percentual de ativação inválido. Revise antes de fechar.');
  return Math.round((dados.basesPendentes.pn*dados.percentuais.pn + dados.basesPendentes.ha*dados.percentuais.ha) + Number.EPSILON) / 100;
}

export function aplicarNegativoNoFinal(valorAntesAtivacao: number, desconto: number): number {
  if (![valorAntesAtivacao,desconto].every(v => Number.isFinite(v) && v >= 0)) throw new Error('Valor final de produtividade inválido.');
  return Math.max(0,Math.round((valorAntesAtivacao-desconto)*100)/100);
}

export function calcularDescontoLinhasAtivacao(linhas: Array<{ base: number; categoria: 'pn' | 'ha'; pendente: boolean; foraSul?: boolean }>,
  config: Omit<Parameters<typeof calcularNegativoProdutividadeAtivacao>[0], 'basesPendentes'>): number {
  const centavos = linhas.filter(l => l.pendente).reduce((total,l) => {
    const fator = l.foraSul ? FATOR_COMISSAO_FORA_SUL : 1;
    return total + Math.round(100*calcularNegativoProdutividadeAtivacao({
      ...config,basesPendentes: { pn: l.categoria === 'pn' ? Number(l.base)*fator : 0,ha: l.categoria === 'ha' ? Number(l.base)*fator : 0 },
    }));
  },0);
  return centavos/100;
}

/** O que a pessoa ganha naquela linha (Douglas 30/09/2026): valor da linha ×
 *  % de PN/HA dela, metade fora do Sul. Validada = ganha; sem validar = é o
 *  que sai da comissão. */
export function valorComissaoLinha(l: { base: number; categoria: 'pn' | 'ha'; foraSul?: boolean }, percentuais: { pn: number; ha: number }): number {
  const pct = l.categoria === 'pn' ? percentuais.pn : percentuais.ha;
  const fator = l.foraSul ? FATOR_COMISSAO_FORA_SUL : 1;
  if (![Number(l.base),pct].every(v => Number.isFinite(v) && v >= 0)) return 0;
  return Math.round(Number(l.base)*fator*pct + Number.EPSILON) / 100;
}

/**
 * DESCONTO DO eSIM NÃO VALIDADO — consultor, supervisor e gerente (Douglas 30/09/2026).
 * Deflator como a convergência e o cancelado: não mexe em meta nem faixa, só
 * na comissão, e só de quem bateu a meta. Cada um perde o que ganharia naquela
 * linha, com o SEU % sobre as linhas do SEU escopo (gestor = a equipe).
 * Enquanto o prazo corre (até o último dia do mês seguinte) o desconto é o
 * provisório de hoje: validou, some. Depois do prazo vale o fechamento; para o
 * consultor, com os lançamentos já gravados (descontoAtivacaoPessoa).
 */
export function descontoEsim(base: BaseAtivacaoProdutividade | undefined, dados: {
  pessoaId: string | undefined; idsEscopo: string[]; cargo: string; bateuMeta: boolean; percentuais: { pn: number; ha: number };
}): number {
  if (!base?.habilitada || !dados.pessoaId) return 0;
  if (dados.cargo === 'CONSULTOR' && base.prazo_encerrado)
    return descontoAtivacaoPessoa(base,dados.pessoaId,dados.cargo,dados.bateuMeta,dados.percentuais);
  if (!dados.bateuMeta) return 0;
  const escopo = new Set(dados.idsEscopo.length ? dados.idsEscopo : [dados.pessoaId]);
  const centavos = base.linhas.filter(l => l.pendente && escopo.has(l.consultorId))
    .reduce((t,l) => t + Math.round(100*valorComissaoLinha(l,dados.percentuais)),0);
  return centavos/100;
}

/** Escopo do desconto do gestor: só os consultores cuja venda paga a comissão
 *  dele. A Diretoria sai (Douglas 26/10/2026): a venda dela não paga o gestor,
 *  então a linha dela sem eSIM validado também não desconta. Tela, folha e Excel. */
export function escopoEsimDoGestor(consultores: Array<{ consultor_id: string; da_diretoria?: boolean | null; fora_da_meta?: boolean | null }>): string[] {
  return consultores.filter(c => !c.fora_da_meta && !c.da_diretoria).map(c => c.consultor_id);
}

export interface BaseAtivacaoProdutividade {
  habilitada: boolean; prazo_encerrado: boolean;
  linhas: Array<{ consultorId: string; itemId: string; ordem: number; base: number; categoria: 'pn' | 'ha'; pendente: boolean; tipo: string | null; foraSul?: boolean }>;
  negativos: Array<{ consultorId: string; itemId: string; ordem: number; valor: number }>;
}
export function descontoAtivacaoPessoa(base: BaseAtivacaoProdutividade | undefined, pessoaId: string | undefined,
  cargo: string,bateuMeta: boolean,percentuais: { pn: number; ha: number }): number {
  if (!base?.habilitada || !base.prazo_encerrado || cargo !== 'CONSULTOR' || !pessoaId) return 0;
  const anteriores = (base.negativos || []).filter(n => n.consultorId === pessoaId);
  const chaves = new Set(anteriores.map(n => `${n.itemId}:${n.ordem}`));
  const fixo = anteriores.reduce((s,n) => s+Number(n.valor),0);
  if (!Number.isFinite(fixo) || fixo<0) throw new Error('Lançamento original inválido.');
  const novas = base.linhas.filter(l => l.consultorId === pessoaId && !chaves.has(`${l.itemId}:${l.ordem}`));
  return Math.round(100*(fixo+calcularDescontoLinhasAtivacao(novas,{
    habilitada:true,prazoEncerrado:true,cargo,bateuMeta,percentuais,
  })))/100;
}
